import React, { useEffect, useRef, useState, memo } from "react";
import { motion } from "framer-motion";

// Leaf
const Leaf = memo(({ delay, size = 3 }) => (
  <motion.div
    className={`absolute w-${size} h-${size} bg-green-400 rounded-full`}
    initial={{ y: 0, x: 0, opacity: 0, rotate: 0 }}
    animate={{
      y: -120 - Math.random() * 60,
      x: Math.random() * 80 - 40,
      opacity: [0, 1, 0],
      rotate: [0, Math.random() * 360, Math.random() * 720],
    }}
    transition={{
      delay,
      duration: 4 + Math.random() * 2,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut",
    }}
  />
));

// Sprout
const Sprout = memo(({ left = 0, top = 0, delay = 0 }) => (
  <motion.div
    className="absolute w-2 h-6 bg-green-500 rounded-full origin-bottom shadow-md"
    style={{ left, top }}
    initial={{ scaleY: 0, rotate: -15 }}
    animate={{ scaleY: 1, rotate: 0 }}
    transition={{ delay, duration: 1.5, type: "spring", stiffness: 220, damping: 15 }}
  />
));

// Spark
const Spark = memo(() => (
  <motion.div
    className="absolute w-3 h-3 bg-white rounded-full mix-blend-screen opacity-50 shadow-lg"
    initial={{ scale: 0 }}
    animate={{ scale: [0, 1, 0] }}
    transition={{ duration: 1.2, repeat: Infinity, repeatType: "loop" }}
  />
));

// Animal
const Animal = memo(({ type, left, delay }) => {
  const size = type === "cow" ? 16 : type === "sheep" ? 12 : 8;
  const color = type === "cow" ? "bg-black" : type === "sheep" ? "bg-white" : "bg-yellow-400";

  return (
    <motion.div
      className={`absolute ${color} rounded-full shadow-md`}
      style={{ width: size, height: size, left: `${left}%`, bottom: 20 }}
      initial={{ y: 0 }}
      animate={{ y: [0, -5, 0] }}
      transition={{
        delay,
        duration: 2 + Math.random(),
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      }}
    />
  );
});

const letterVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, type: "spring", stiffness: 200, damping: 20 },
  }),
};

const sloganVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08 + 2, type: "spring", stiffness: 200, damping: 20 },
  }),
};

const Intro = ({ onFinish }) => {
  const fiSoundRef = useRef(null);
  const voiceRef = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    // Preload sounds
    fiSoundRef.current = new Audio("/fi-pop.mp3");
    voiceRef.current = new Audio("/farminvest-manly.mp3");
    fiSoundRef.current.preload = "auto";
    voiceRef.current.preload = "auto";
  }, []);

  const startIntro = () => {
    setStarted(true);

    setTimeout(() => fiSoundRef.current?.play(), 300);
    setTimeout(() => fiSoundRef.current?.play(), 700);
    setTimeout(() => voiceRef.current?.play(), 1200);

    setTimeout(() => onFinish?.(), 5500);
  };

  const title = "Farm Invest";
  const slogan = "Sow today, invest for life";

  return (
    <div
      className="relative flex justify-center items-center h-screen overflow-hidden bg-black"
      style={{
        backgroundImage: "url('/images/intro.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-black/20"></div>

      {/* Animate leaves immediately */}
      {[...Array(15)].map((_, i) => (
        <Leaf key={i} delay={i * 0.25} size={2 + Math.floor(Math.random() * 3)} />
      ))}

      {/* Animate animals immediately */}
      {["cow", "sheep", "chicken"].map((type, idx) =>
        [...Array(5)].map((_, i) => (
          <Animal key={`${type}-${i}`} type={type} left={10 + i * 15 + idx * 5} delay={i * 0.3} />
        ))
      )}

      {/* F & I letters */}
      <div className="relative flex z-10 mb-6">
        <motion.div
          className="text-8xl font-extrabold text-green-700 relative mr-12"
          initial={{ scale: 0, x: -50 }}
          animate={{ scale: 1, x: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
        >
          F
          <Sprout left={12} top={-22} delay={0.4} />
          <Sprout left={4} top={-16} delay={0.7} />
          <Spark />
        </motion.div>
        <motion.div
          className="text-8xl font-extrabold text-green-700 relative"
          initial={{ scale: 0, x: 50 }}
          animate={{ scale: 1, x: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 25, delay: 0.3 }}
        >
          I
          <Sprout left={0} top={-20} delay={0.5} />
          <Sprout left={2} top={-24} delay={0.8} />
          <Spark />
        </motion.div>
      </div>

      {/* Letter by letter Farm Invest */}
      {started && (
        <div className="text-center text-gray-100 absolute mt-32 z-10">
          <motion.div className="text-5xl font-bold flex justify-center">
            {title.split("").map((char, i) => (
              <motion.span key={i} custom={i} variants={letterVariants} initial="hidden" animate="visible">
                {char === " " ? "\u00A0" : char}
              </motion.span>
            ))}
          </motion.div>
          <motion.div className="text-2xl font-semibold mt-2 flex justify-center">
            {slogan.split("").map((char, i) => (
              <motion.span key={i} custom={i} variants={sloganVariants} initial="hidden" animate="visible">
                {char === " " ? "\u00A0" : char}
              </motion.span>
            ))}
          </motion.div>
        </div>
      )}

      {/* Tap to start overlay */}
      {!started && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/50 z-50 cursor-pointer"
          onClick={startIntro}
        >
          <span className="text-white text-2xl font-bold">Tap to start</span>
        </div>
      )}

      <audio ref={fiSoundRef} src="/fi-pop.mp3" preload="auto" />
      <audio ref={voiceRef} src="/farminvest-manly.mp3" preload="auto" />
    </div>
  );
};

export default Intro;